export const AdvancedMultiSelect = {
  mounted() {
    this.lastChecked = null;
    this.checkboxes = [];
    this.checkedValues = new Set();

    // Store bound functions as properties so we can reference them in 'updated()' & 'destroyed()'
    this.boundHandleCheck = this.handleCheck.bind(this)
    this.boundHandleSelectAll = this.handleSelectAll.bind(this)
    this.boundHandleUncheckLetters = this.handleUncheckLetters.bind(this)

    this.handleCheckForAllCheckboxes();

    this.handleEvent("selection:reset", () => {
      this.checkedValues.clear();
      this.lastChecked = null;
    });

    this.handleEvent("selection:sync", ({ ids }) => {
      this.checkedValues = new Set(ids);
      this.lastChecked = null;
    });
  },

  updated() {
    this.handleCheckForAllCheckboxes();

    // Restore checked state — LiveView may patch stream rows with stale server-side
    // checked={false} while the client has already checked them (race between phx-change
    // events and handle_async page loads). The hook is the source of truth.
    this.checkboxes.forEach(cb => {
      if (this.checkedValues.has(cb.value)) {
        cb.checked = true;
      }
      this.applyStyle(cb);
    });
  },

  destroyed() {
    this.removeListeners();
  },

  handleCheckForAllCheckboxes() {
    this.removeListeners();

    this.checkboxes = Array.from(this.el.querySelectorAll('input[type="checkbox"]'));
    this.addListeners();
  },

  addListeners() {
    this.checkboxes.forEach(checkbox => checkbox.addEventListener('click', this.boundHandleCheck));
    window.addEventListener('phx:letters-table:check_all_checkboxes', this.boundHandleSelectAll);
    window.addEventListener('phx:letters-table:uncheck_letters', this.boundHandleUncheckLetters);
  },

  removeListeners() {
    this.checkboxes.forEach(checkbox => checkbox.removeEventListener('click', this.boundHandleCheck));
    window.removeEventListener('phx:letters-table:check_all_checkboxes', this.boundHandleSelectAll);
    window.removeEventListener('phx:letters-table:uncheck_letters', this.boundHandleUncheckLetters);
  },

  handleCheck(e) {
    let inBetween = false;
    let anyChecked = false;

    this.checkboxes.forEach(checkbox => {
      if (e.shiftKey) {
        // This keep track of where we are in the range of checkboxes
        if (this.lastChecked != null && checkbox === e.target || checkbox === this.lastChecked) {
          inBetween = !inBetween;
        }

        // This makes sure that the checkboxes in between the last checked and the current checkbox are checked
        if (inBetween) {
          checkbox.checked = e.target.checked;
        }

        // This makes sure that the last checked checkbox is also unchecked when unselecting by going backwards
        if (this.lastChecked != null) {
          this.lastChecked.checked = e.target.checked;
        }
      }

      if (checkbox.checked) {
        anyChecked = true;
      }

      this.applyStyle(checkbox);
    });

    // This keeps track of the last checked checkbox, if any.
    if (anyChecked) {
      if (e.target.checked) {
        this.lastChecked = e.target;
      }
    } else {
      this.lastChecked = null;
    }

    // Keep checkedValues in sync so updated() can restore state after LiveView patches
    this.checkboxes.forEach(cb => {
      if (cb.checked) {
        this.checkedValues.add(cb.value);
      } else {
        this.checkedValues.delete(cb.value);
      }
    });
  },

  handleSelectAll(e) {
    this.checkboxes.forEach(checkbox => {
      checkbox.checked = e.detail.checked;
      this.applyStyle(checkbox);
      if (e.detail.checked) {
        this.checkedValues.add(checkbox.value);
      } else {
        this.checkedValues.delete(checkbox.value);
      }
    });
  },

  handleUncheckLetters(e) {
    const ids = new Set(e.detail.ids.map(String));
    this.checkboxes.forEach(checkbox => {
      if (ids.has(checkbox.value)) {
        checkbox.checked = false;
        this.checkedValues.delete(checkbox.value);
        this.applyStyle(checkbox);
      }
    });
  },

  applyStyle(checkbox) {
    // We have to apply styling client-side because LV won't re-render the rows in the table, as we're using a stream
    const row = checkbox.closest('tr')
    selectedClasses = row.dataset.multiselectSelectedClass.split(" ");
    unselectedClasses = row.dataset.multiselectUnselectedClass.split(" ");

    if (checkbox.checked) {
      row.classList.add(...selectedClasses);
      row.classList.remove(...unselectedClasses);
    } else {
      row.classList.remove(...selectedClasses);
      row.classList.add(...unselectedClasses);
    }
  }
}
